<template>
    <div class="match-making-tab">
        <div class="snippet general-event-config-snippet">
            <div class="form-group row">
                <div class="col-lg-12">
                    <h4 class="mb-3">Match Making Configuration</h4>
                    <p class="text-muted mb-4">Add keywords to help attendees find this {{ moduleDisplayName }} through match making.</p>
                </div>
            </div>
            
            <!-- Keywords Input with Autocomplete -->
            <div class="form-group row">
                <div class="col-lg-12">
                    <label class="font-weight-bold">Match Making Keywords</label>
                    <div class="keywords-input-wrapper">
                        <vue3-tags-input 
                            :tags="keywords" 
                            placeholder="Enter keywords and press Enter" 
                            @on-tags-changed="handleTagsChange"
                            :max-tags="maxTags"
                            :add-only-from-autocomplete="false"
                            :class="{ 'is-invalid': hasError }"
                            ref="tagsInput"
                        />
                        
                        <!-- Custom Dropdown for Available Keywords -->
                        <div v-if="showKeywordDropdown && availableKeywords.length > 0" class="keywords-dropdown">
                            <div class="dropdown-header">
                                <i class="fa fa-lightbulb-o"></i> Available Match Making Keywords
                            </div>
                            <div class="dropdown-list">
                                <div 
                                    v-for="keyword in availableKeywords" 
                                    :key="keyword"
                                    class="dropdown-item"
                                    @click="addKeywordFromDropdown(keyword)"
                                >
                                    <i class="fa fa-tag"></i>
                                    {{ keyword }}
                                    <span class="badge">Click to add</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <small class="text-muted">Add relevant keywords to help match this {{ moduleDisplayName }} with attendee interests. Keywords are case-insensitive (e.g., "AI" and "ai" are treated as the same).</small>
                    <div v-if="hasError" class="invalid-feedback d-block">
                        Please add at least one keyword
                    </div>
                </div>
            </div>
            
            <!-- Available Keywords Section -->
            <div class="form-group row mt-3" v-if="allEventKeywords && allEventKeywords.length > 0">
                <div class="col-lg-12">
                    <label class="font-weight-bold">
                        Available Match Making Keywords 
                        <span class="badge badge-secondary">{{ allEventKeywords.length }} total</span>
                    </label>
                    <div class="available-keywords">
                        <span 
                            v-for="keyword in allEventKeywords" 
                            :key="keyword" 
                            class="badge keyword-badge"
                            :class="{ 'keyword-added': isKeywordAdded(keyword) }"
                            @click="!isKeywordAdded(keyword) && addKeywordFromList(keyword)"
                        >
                            {{ keyword }}
                            <i v-if="!isKeywordAdded(keyword)" class="fa fa-plus-circle add-icon"></i>
                            <i v-else class="fa fa-check-circle check-icon"></i>
                        </span>
                    </div>
                    <small class="text-muted">Click on any keyword to add it to your {{ moduleDisplayName }}. Case-insensitive matching applied.</small>
                </div>
            </div>
            
            <!-- Current Keywords Display -->
            <div class="form-group row mt-3" v-if="existingKeywords && existingKeywords.length && !loadingMatchMaking">
                <div class="col-lg-12">
                    <label class="font-weight-bold">Current Keywords for this {{ moduleDisplayName }}</label>
                    <div class="existing-keywords">
                        <span v-for="keyword in existingKeywords" :key="keyword" class="badge badge-info existing-keyword">
                            {{ keyword }}
                        </span>
                    </div>
                </div>
            </div>
            
            <!-- Loading State -->
            <div class="form-group row mt-3" v-if="loadingEventKeywords || loadingMatchMaking">
                <div class="col-lg-12 text-center">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                    <p>Loading match making data...</p>
                </div>
            </div>
            
            <!-- Save Button and Status -->
            <div class="form-group row mt-4" v-if="showSaveButton">
                <div class="col-lg-12">
                    <div class="d-flex justify-content-between align-items-center">
                        <button 
                            type="button" 
                            class="btn btn-primary" 
                            @click="saveMatchMakingData"
                            :disabled="loadingMatchMaking || saving || !hasChanges"
                        >
                            <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                            {{ saving ? 'Saving...' : 'Save Keywords' }}
                        </button>
                        
                        <div v-if="saveStatus" class="save-status" :class="saveStatus.type">
                            <i :class="saveStatus.icon"></i>
                            {{ saveStatus.message }}
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Match Making Info -->
            <div class="alert alert-info mt-4" role="alert">
                <i class="fa fa-info-circle"></i>
                <strong>How it works:</strong> Keywords help attendees discover your content through our match making algorithm. Add terms like topics, technologies, industries, or any relevant terms.
                <br><br>
                <strong>Note:</strong> Keywords are case-insensitive - "AI", "ai", and "Ai" are treated as the same keyword.
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Vue3TagsInput from 'vue3-tags-input';
import _ from 'lodash';

export default {
    name: 'MatchMakingTab',
    components: {
        Vue3TagsInput
    },
    props: {
        moduleId: {
            type: String,
            required: true
        },
        eventId: {
            type: String,
            required: true
        },
        moduleName: {
            type: String,
            required: true,
            validator: (value) => {
                const validModules = [
                    'Live-Presentation',
                    'Panel-Discussion',
                    'Booth',
                    'Round-Table',
                    'Display-Center',
                    'Meeting',
                    'Agenda-On-Demand'
                ];
                return validModules.includes(value);
            }
        },
        moduleDisplayName: {
            type: String,
            default: 'item'
        },
        maxTags: {
            type: Number,
            default: 20
        },
        autoSave: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
        showSaveButton: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            keywords: [],
            originalKeywords: [],
            hasError: false,
            saving: false,
            saveStatus: null,
            autoSaveTimer: null,
            showKeywordDropdown: false
        };
    },
    computed: {
        ...mapGetters("EventKeywordsIndex", {
            allEventKeywords: 'item',
            loadingEventKeywords: 'loading'
        }),
        ...mapGetters('MatchMakingSingle', {
            loadingMatchMaking: 'loading',
            existingKeywords: 'existingKeywords',
            saved: 'saved'
        }),
        
        // Get normalized keywords (case-insensitive comparison)
        normalizedKeywords() {
            return this.keywords.map(k => k.toLowerCase());
        },
        
        // Get available keywords (not already added) - case-insensitive
        availableKeywords() {
            if (!this.allEventKeywords || !this.allEventKeywords.length) return [];
            return this.allEventKeywords.filter(keyword => 
                !this.isKeywordAdded(keyword)
            );
        },
        
        // Fixed computed property without side effects
        hasChanges() {
            const currentSorted = [...this.keywords].sort();
            const originalSorted = [...this.originalKeywords].sort();
            return JSON.stringify(currentSorted) !== JSON.stringify(originalSorted);
        }
    },
    watch: {
        eventId: {
            handler(newEventId) {
                if (newEventId) {
                    this.fetchAllEventKeywords();
                    this.fetchMatchMakingData();
                }
            },
            immediate: true
        },
        
        moduleId: {
            handler(newVal) {
                if (newVal && this.eventId) {
                    this.fetchMatchMakingData();
                }
            },
            immediate: true
        },
        
        keywords: {
            handler(newKeywords) {
                if (this.autoSave && this.hasChanges) {
                    if (this.autoSaveTimer) {
                        clearTimeout(this.autoSaveTimer);
                    }
                    this.autoSaveTimer = setTimeout(() => {
                        this.saveMatchMakingData();
                    }, 1000);
                }
            },
            deep: true
        },
        
        // Show dropdown when focusing on tags input
        'showKeywordDropdown': {
            handler(val) {
                if (val) {
                    // Auto hide after 5 seconds
                    setTimeout(() => {
                        this.showKeywordDropdown = false;
                    }, 5000);
                }
            }
        }
    },
    mounted() {
        this.resetError();
        
        // Add click outside listener for dropdown
        document.addEventListener('click', this.handleClickOutside);
        
        // Add focus listener to tags input
        this.$nextTick(() => {
            const tagsInput = this.$refs.tagsInput?.$el?.querySelector('.ti-input');
            if (tagsInput) {
                tagsInput.addEventListener('focus', () => {
                    if (this.availableKeywords.length > 0) {
                        this.showKeywordDropdown = true;
                    }
                });
            }
        });
    },
    beforeUnmount() {
        if (this.autoSaveTimer) {
            clearTimeout(this.autoSaveTimer);
        }
        this.resetState();
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        ...mapActions("EventKeywordsIndex", ['fetchData']),
        ...mapActions('MatchMakingSingle', [
            'fetchMatchMaking',
            'saveMatchMaking',
            'resetState'
        ]),
        
        /**
         * Normalize keyword to lowercase for comparison
         */
        normalizeKeyword(keyword) {
            return keyword ? keyword.toLowerCase().trim() : '';
        },
        
        /**
         * Check if keyword is already added (case-insensitive)
         */
        isKeywordAdded(keyword) {
            const normalizedKeyword = this.normalizeKeyword(keyword);
            return this.keywords.some(k => this.normalizeKeyword(k) === normalizedKeyword);
        },
        
        /**
         * Find existing keyword by case-insensitive match
         */
        findExistingKeyword(keyword) {
            const normalizedSearch = this.normalizeKeyword(keyword);
            return this.keywords.find(k => this.normalizeKeyword(k) === normalizedSearch);
        },
        
        /**
         * Add keyword with case-insensitive duplicate checking
         */
        addKeyword(keyword) {
            if (!keyword || keyword.trim() === '') return false;
            
            const trimmedKeyword = keyword.trim();
            
            // Check if keyword already exists (case-insensitive)
            if (this.isKeywordAdded(trimmedKeyword)) {
                this.showSaveStatus('info', `Keyword "${trimmedKeyword}" already exists`);
                return false;
            }
            
            // Check max tags limit
            if (this.keywords.length >= this.maxTags) {
                this.showSaveStatus('error', `Maximum ${this.maxTags} keywords allowed`);
                return false;
            }
            
            // Add the keyword (preserve original case from input if custom, or from available list)
            this.keywords.push(trimmedKeyword);
            this.hasError = false;
            this.clearSaveStatus();
            return true;
        },
        
        /**
         * Fetch all event keywords from EventKeywordsIndex store
         */
        async fetchAllEventKeywords() {
            if (!this.eventId) return;
            
            try {
                await this.fetchData({ event_id: this.eventId });
            } catch (error) {
                console.error('Error fetching event keywords:', error);
            }
        },
        
        /**
         * Fetch match making data for current module
         */
        async fetchMatchMakingData() {
            if (!this.moduleId || !this.eventId) return;
            
            try {
                await this.fetchMatchMaking({
                    eventId: this.eventId,
                    moduleId: this.moduleId,
                    moduleName: this.moduleName
                });
                
                this.keywords = [...this.existingKeywords];
                this.originalKeywords = [...this.existingKeywords];
                this.clearSaveStatus();
            } catch (error) {
                console.error('Error fetching match making data:', error);
                this.keywords = [];
                this.originalKeywords = [];
            }
        },
        
        /**
         * Handle tags change from vue3-tags-input
         */
        handleTagsChange(tags) {
            // Normalize and remove duplicates case-insensitively
            const uniqueTags = [];
            const normalizedSet = new Set();
            
            for (const tag of tags) {
                const normalizedTag = this.normalizeKeyword(tag);
                if (!normalizedSet.has(normalizedTag) && tag.trim() !== '') {
                    normalizedSet.add(normalizedTag);
                    uniqueTags.push(tag);
                }
            }
            
            this.keywords = uniqueTags;
            this.hasError = false;
            this.clearSaveStatus();
        },
        
        /**
         * Add keyword from dropdown
         */
        addKeywordFromDropdown(keyword) {
            this.addKeyword(keyword);
            this.showKeywordDropdown = false;
        },
        
        /**
         * Add keyword from available keywords list
         */
        addKeywordFromList(keyword) {
            this.addKeyword(keyword);
        },
        
        /**
         * Handle click outside to close dropdown
         */
        handleClickOutside(event) {
            const wrapper = this.$el.querySelector('.keywords-input-wrapper');
            if (wrapper && !wrapper.contains(event.target)) {
                this.showKeywordDropdown = false;
            }
        },
        
        /**
         * Save match making data
         */
        async saveMatchMakingData() {
            if (this.required && (!this.keywords || this.keywords.length === 0)) {
                this.hasError = true;
                this.showSaveStatus('error', 'Please add at least one keyword');
                throw new Error('Keywords are required');
            }
            
            if (!this.moduleId || !this.eventId) {
                this.showSaveStatus('error', 'Module ID and Event ID are required');
                throw new Error('Module ID and Event ID are required');
            }
            
            if (!this.hasChanges) {
                this.showSaveStatus('info', 'No changes to save');
                return;
            }
            
            this.saving = true;
            this.clearSaveStatus();
            
            try {
                await this.saveMatchMaking({
                    eventId: this.eventId,
                    moduleId: this.moduleId,
                    moduleName: this.moduleName,
                    keywords: this.keywords
                });
                
                this.originalKeywords = [...this.keywords];
                this.showSaveStatus('success', 'Keywords saved successfully!');
                
                // Emit event to parent
                this.$emit('saved', {
                    moduleId: this.moduleId,
                    moduleName: this.moduleName,
                    keywords: this.keywords
                });
                
                return true;
            } catch (error) {
                console.error('Error saving match making:', error);
                this.showSaveStatus('error', error.response?.data?.message || 'Failed to save keywords');
                throw error;
            } finally {
                this.saving = false;
                
                // Auto hide success message after 3 seconds
                if (this.saveStatus?.type === 'success') {
                    setTimeout(() => {
                        this.clearSaveStatus();
                    }, 3000);
                }
            }
        },
        
        /**
         * Show save status message
         */
        showSaveStatus(type, message) {
            const statusConfig = {
                success: {
                    icon: 'fa fa-check-circle',
                    class: 'text-success'
                },
                error: {
                    icon: 'fa fa-exclamation-circle',
                    class: 'text-danger'
                },
                info: {
                    icon: 'fa fa-info-circle',
                    class: 'text-info'
                }
            };
            
            this.saveStatus = {
                type: type,
                message: message,
                icon: statusConfig[type].icon,
                class: statusConfig[type].class
            };
            
            // Auto clear info messages after 2 seconds
            if (type === 'info') {
                setTimeout(() => {
                    if (this.saveStatus?.type === 'info') {
                        this.clearSaveStatus();
                    }
                }, 2000);
            }
        },
        
        /**
         * Clear save status
         */
        clearSaveStatus() {
            this.saveStatus = null;
        },
        
        /**
         * Validate keywords
         */
        validate() {
            if (this.required && (!this.keywords || this.keywords.length === 0)) {
                this.hasError = true;
                return false;
            }
            return true;
        },
        
        /**
         * Reset error state
         */
        resetError() {
            this.hasError = false;
        },
        
        /**
         * Get keywords for parent component
         */
        getKeywords() {
            return this.keywords;
        },
        
        /**
         * Set keywords from parent
         */
        setKeywords(keywords) {
            // Remove duplicates case-insensitively
            const uniqueKeywords = [];
            const normalizedSet = new Set();
            
            for (const keyword of keywords) {
                const normalizedKeyword = this.normalizeKeyword(keyword);
                if (!normalizedSet.has(normalizedKeyword)) {
                    normalizedSet.add(normalizedKeyword);
                    uniqueKeywords.push(keyword);
                }
            }
            
            this.keywords = uniqueKeywords;
            this.originalKeywords = [...uniqueKeywords];
        },
        
        /**
         * Reset to original keywords
         */
        resetToOriginal() {
            this.keywords = [...this.originalKeywords];
            this.hasError = false;
            this.clearSaveStatus();
        },
        
        /**
         * Check if there are unsaved changes
         */
        hasUnsavedChanges() {
            return this.hasChanges;
        }
    },
    expose: ['validate', 'saveMatchMakingData', 'getKeywords', 'setKeywords', 'resetError', 'resetToOriginal', 'hasUnsavedChanges']
};
</script>

<style lang="scss" scoped>
.match-making-tab {
    .keywords-input-wrapper {
        position: relative;
        
        .keywords-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #ddd;
            border-radius: 8px;
            margin-top: 5px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 1000;
            max-height: 300px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            
            .dropdown-header {
                padding: 10px 12px;
                background: #f8f9fa;
                border-bottom: 1px solid #e9ecef;
                font-weight: 600;
                font-size: 0.875rem;
                color: #495057;
                
                i {
                    margin-right: 6px;
                    color: #0088CC;
                }
            }
            
            .dropdown-list {
                overflow-y: auto;
                max-height: 250px;
                
                .dropdown-item {
                    padding: 8px 12px;
                    cursor: pointer;
                    transition: background 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.875rem;
                    border-bottom: 1px solid #f1f3f5;
                    
                    i {
                        color: #0088CC;
                        font-size: 0.75rem;
                    }
                    
                    .badge {
                        margin-left: auto;
                        background-color: #e9ecef;
                        color: #495057;
                        font-size: 0.7rem;
                        padding: 2px 6px;
                    }
                    
                    &:hover {
                        background-color: #f0f9ff;
                        
                        .badge {
                            background-color: #0088CC;
                            color: white;
                        }
                    }
                    
                    &:last-child {
                        border-bottom: none;
                    }
                }
            }
        }
    }
    
    .available-keywords {
        margin-top: 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        
        .keyword-badge {
            background-color: #e9ecef;
            color: #495057;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 0.875rem;
            cursor: pointer;
            transition: all 0.2s;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            
            &:hover:not(.keyword-added) {
                background-color: #0088CC;
                color: white;
                transform: translateY(-1px);
            }
            
            &.keyword-added {
                background-color: #28a745;
                color: white;
                cursor: default;
                opacity: 0.8;
            }
            
            .add-icon,
            .check-icon {
                font-size: 0.75rem;
            }
        }
    }
    
    .existing-keywords {
        margin-top: 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }
    
    .existing-keyword {
        background-color: #6c757d;
        color: white;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 0.875rem;
    }
    
    .save-status {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.875rem;
        
        i {
            font-size: 1rem;
        }
        
        &.text-success {
            color: #28a745;
        }
        
        &.text-danger {
            color: #dc3545;
        }
        
        &.text-info {
            color: #17a2b8;
        }
    }
    
    :deep(.vue3-tags-input) {
        .ti-input {
            border-radius: 8px;
            border: 1px solid #ddd;
            padding: 8px;
            min-height: 60px;
            
            &:focus-within {
                border-color: #0088CC;
                box-shadow: 0 0 0 0.2rem rgba(0, 136, 204, 0.25);
            }
            
            &.is-invalid {
                border-color: #dc3545;
                
                &:focus-within {
                    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
                }
            }
        }
        
        .ti-tag {
            background-color: #0088CC;
            color: white;
            border-radius: 4px;
            padding: 4px 8px;
            margin: 2px;
            font-size: 0.875rem;
            
            .ti-tag-close {
                color: white;
                margin-left: 6px;
                
                &:hover {
                    background-color: rgba(255, 255, 255, 0.2);
                    border-radius: 50%;
                }
            }
        }
        
        .ti-input-field {
            padding: 4px;
            font-size: 0.875rem;
        }
    }
    
    .spinner-border {
        width: 1rem;
        height: 1rem;
        margin-right: 8px;
    }
    
    .btn-primary {
        background-color: #0088CC;
        border-color: #0088CC;
        
        &:hover:not(:disabled) {
            background-color: #006699;
            border-color: #006699;
        }
        
        &:disabled {
            opacity: 0.65;
            cursor: not-allowed;
        }
    }
    
    .alert-info {
        background-color: #d1ecf1;
        border-color: #bee5eb;
        color: #0c5460;
        border-radius: 8px;
        
        i {
            margin-right: 8px;
        }
    }
    
    .badge-secondary {
        background-color: #6c757d;
        font-size: 0.7rem;
        vertical-align: middle;
    }
    
    @media (max-width: 768px) {
        .existing-keyword,
        .keyword-badge {
            font-size: 0.75rem;
            padding: 4px 8px;
        }
        
        :deep(.vue3-tags-input) {
            .ti-tag {
                font-size: 0.75rem;
                padding: 2px 6px;
            }
        }
        
        .d-flex {
            flex-direction: column;
            gap: 10px;
            
            .save-status {
                justify-content: center;
            }
        }
        
        .keywords-dropdown {
            .dropdown-item {
                font-size: 0.75rem;
                padding: 6px 10px;
            }
        }
    }
}
</style>